import { useCallback } from 'react'

import { ENTITY_TYPES } from '@sales-center/common'
import type { IUpdateContactFormModel } from '../typings'
import type { IContactInput } from '@user/store/contacts'
import { useCreateUpdateLeadContact } from '@user/store/lead'
import { useCreateUpdateOpportunityContact } from '@user/store/opportunity'
import { formatEmailAddresses } from '@user/utils/formatEmailAddresses'

export function useHandlers(props: {
  entityId: number
  entityType: ENTITY_TYPES
  contactId?: number
  onClose: () => void
}) {
  const { entityId, entityType, contactId, onClose } = props

  const createUpdateLeadContact = useCreateUpdateLeadContact()
  const createUpdateOpportunityContact = useCreateUpdateOpportunityContact()

  const onSubmit = useCallback(
    async (model: IUpdateContactFormModel) => {
      const contactInput: IContactInput = {
        altPhoneExt: model.altPhoneExt,
        altPhoneNumber: model.altPhoneNumber,
        city: model.city,
        company: model.company,
        contactMethod: model.contactMethod,
        contactTime: model.contactTime.join(','),
        emailAddresses: formatEmailAddresses(model.emailAddresses),
        firstName: model.firstName,
        id: contactId,
        isPrimaryContact: model.isPrimaryContact,
        lastName: model.lastName,
        phoneExt: model.phoneExt,
        phoneNumber: model.phoneNumber,
        postalCode: model.postalCode,
        county: model.county,
        stateAbbreviation: model.state,
        street1: model.address1,
        street2: model.address2,
        timeZone: model.timeZone,
        leadId: entityType === ENTITY_TYPES.Lead ? entityId ?? null : null,
        opportunityId:
          entityType === ENTITY_TYPES.Opportunity ? entityId ?? null : null,
      }

      if (entityType === ENTITY_TYPES.Lead) {
        await createUpdateLeadContact({
          leadId: entityId,
          contact: contactInput,
          isPrimaryContact: contactInput.isPrimaryContact,
        })
      } else {
        await createUpdateOpportunityContact({
          contact: contactInput,
          opportunityId: entityId,
          isPrimaryContact: contactInput.isPrimaryContact,
        })
      }

      onClose()
    },
    [
      contactId,
      createUpdateLeadContact,
      createUpdateOpportunityContact,
      entityId,
      entityType,
      onClose,
    ],
  )

  return {
    onSubmit,
  }
}
